"use client";

import Link from "next/link";
import type { SafeNavigationItem, SocialLinkType } from "@/lib/directus/typeGuards";
import { ImageDataType } from "@/shared/types";
import type { ButtonDataType } from "@/shared/types";
import { CONTENT_FRAME } from "@/shared/components/BlockWrapper";
import { SocialIcon } from "@/shared/components/SocialIcon";
import { getHeaderSocialLinks } from "@/shared/utils/headerSocialLinks";
import { HeaderMainBar } from "./HeaderMainBar";
import type { MenuPromo } from "./DesktopNav";

interface HeaderClientProps {
  logo?: ImageDataType | null;
  navigation?: SafeNavigationItem[] | null;
  headerCta?: ButtonDataType[];
  phone?: string | null;
  phoneSecondary?: string | null;
  email?: string | null;
  address?: string | null;
  socialLinks?: SocialLinkType[] | null;
  menuPromo?: MenuPromo | null;
}

export function HeaderClient({
  logo,
  navigation,
  headerCta,
  phone,
  phoneSecondary,
  email,
  address,
  socialLinks,
  menuPromo,
}: HeaderClientProps) {
  const headerSocialLinks = getHeaderSocialLinks(socialLinks);

  return (
    <header className="site-header fixed left-0 top-0 z-50 w-full bg-background">
      <div className="header-top-bar hidden overflow-hidden bg-header-top desktop:block">
        <div
          className={`${CONTENT_FRAME} flex h-[var(--header-top-height)] items-center justify-between px-6 desktop:px-10`}
          aria-label="Контакты"
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-1 text-xs text-header-top-fg">
            {phone && (
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="shrink-0 transition-opacity hover:opacity-80"
              >
                {phone}
              </a>
            )}
            {phoneSecondary && (
              <a
                href={`tel:${phoneSecondary.replace(/\s/g, "")}`}
                className="shrink-0 transition-opacity hover:opacity-80"
              >
                {phoneSecondary}
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="shrink-0 transition-opacity hover:opacity-80"
              >
                {email}
              </a>
            )}
            {address && (
              <span className="min-w-0 truncate text-header-top-fg/80">
                {address}
              </span>
            )}
          </div>

          {headerSocialLinks.length > 0 && (
            <div className="flex shrink-0 items-center gap-4">
              {headerSocialLinks.map((social) => (
                <Link
                  key={social.service}
                  href={social.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex size-6 items-center justify-center opacity-80 transition-opacity hover:opacity-100"
                  aria-label={social.service}
                >
                  <SocialIcon service={social.service} variant="header" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <HeaderMainBar
        logo={logo}
        navigation={navigation}
        headerCta={headerCta}
        phone={phone}
        phoneSecondary={phoneSecondary}
        email={email}
        address={address}
        socialLinks={socialLinks}
        menuPromo={menuPromo}
      />
    </header>
  );
}
